import {
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Select
} from '@chakra-ui/react'
import type { PersonAdditionalData } from 'BrokerTsLib/src/dtos'
import { Selection, SelectionType } from 'BrokerTsLib/src/enums'
import { useFormContext } from 'BrokerTsLib/src/rhf'

import RuianAddressSelector from '@/components/RuianAddressSelector'
import { FormBody } from '@/containers/forms/FormBody'
import { FormRow } from '@/containers/forms/FormRow'
import AgentPerson from '@/containers/forms/person/AgentPerson'
import ICOOptions from '@/containers/forms/person/ICOOptions'
import InsureeContact from '@/containers/forms/person/InsureeContact'
import InsureeName from '@/containers/forms/person/InsureeName'
import { PersonID } from '@/containers/forms/person/PersonID'
import PersonType from '@/containers/forms/person/PersonType'
import { Agreements } from '@/containers/steps/vehicle/Step3/forms/AdditionalInsureeInfoForm/Agreements'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useSelectedQuote } from '@/context/SelectedQuoteContext'
import { useSelections } from '@/hooks'
import { useNameof } from '@/hooks/useNameof'

export type AdditionalPolicyHolderInfoFormProps = {
  personPrefix: string
}

export const AdditionalPolicyHolderInfoForm = ({
  personPrefix,
}: AdditionalPolicyHolderInfoFormProps) => {
  const { isDisabled } = useInsuranceParams()

  const { offer } = useSelectedQuote()

  const { watch, register, getValues, control } = useFormContext()

  const pnameof = useNameof<PersonAdditionalData>(personPrefix)

  const { data: dataTitles } = useSelections(SelectionType.TitleBefore)

  const pType = watch(pnameof((x) => x.type))
  const showAgent = watch(pnameof((x) => x.agent) + '.required')

  return (
    <>
      <FormBody>
        <PersonType personPrefix={personPrefix} disabled={true} />

        {[Selection.Company, Selection.SelfEmployed].includes(pType) && (
          <FormControl>
            <Flex sx={{ gap: 2 }}>
              <Checkbox
                {...register('policyHolder.person.isVatPayer')}
                isDisabled={true}
              >
                Plátce DPH
              </Checkbox>
            </Flex>
          </FormControl>
        )}

        {[Selection.Person, Selection.SelfEmployed].includes(pType) && (
          <PersonID personPrefix={personPrefix} disabled={true} />
        )}

        {[Selection.Company, Selection.SelfEmployed].includes(pType) && (
          <ICOOptions personPrefix={personPrefix} disabled={true} />
        )}

        {[Selection.Person, Selection.SelfEmployed].includes(pType) && (
          <FormRow itemsCount={3}>
            <FormControl>
              <FormLabel>Titul před jménem</FormLabel>

              {(dataTitles?.data.length ?? 0) > 0 && (
                <Select
                  {...register(pnameof((x) => x.titleBefore))}
                  isDisabled={isDisabled}
                  defaultValue={getValues(pnameof((x) => x.titleBefore)) ?? ''}
                >
                  <option value="">Vyberte</option>
                  {dataTitles?.data.map((item) => (
                    <option value={item.value?.toString()} key={item.id}>
                      {item.value}
                    </option>
                  ))}
                </Select>
              )}
            </FormControl>

            <InsureeName personPrefix={personPrefix} disabled={true} />
          </FormRow>
        )}

        <RuianAddressSelector
          addressPath={pnameof((x) => x.address)}
          isDisabled={isDisabled}
          showOnlyLotNumber={false}
        />

        {showAgent && <AgentPerson personPrefix={personPrefix} />}

        <InsureeContact personPrefix={personPrefix} isDisabled={isDisabled} />

        <Agreements
          control={control}
          agreementName={pnameof((x) => x.agreement)}
          agreementOtherName={pnameof((x) => x.agreementOther)}
          agreementWarningMessageName="policyHolder.agreementWarningMessage"
          isDisabled={isDisabled}
          offer={offer!}
        />
      </FormBody>
    </>
  )
}
